import { IcoSettings16, IcoShuffle16, IcoTrash16 } from '@onefootprint/icons';
import { Divider, LinkButton, type TemplateInputProps, useConfirmationDialog } from '@onefootprint/ui';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SettingsSection from '@/components/playbook-builder/components/shared/settings-section';
import useFocusOnQuestion from '../hooks/use-focus-on-question';
import type { CustomQuestionsFormData } from '../types';
import { AddressForm } from './questions/address';
import { CheckboxForm } from './questions/checkbox';
import { CheckboxesForm } from './questions/checkboxes';
import { DropdownForm } from './questions/dropdown';
import { LongTextForm } from './questions/long-text';
import { MultipleChoiceForm } from './questions/multiple-choice';
import { NumberForm } from './questions/number';
import { ShortTextForm } from './questions/short-text';
import { TextBlockForm } from './questions/text-block';
import SettingsLogic, { type SettingsLogicRef } from './settings-logic';

type QuestionSettingsProps = {
  questionIndex: number;
  onDelete: () => void;
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  canEdit: boolean;
};

export type QuestionSettingsRef = {
  validateBeforeClose: () => Promise<boolean>;
};

const QuestionSettings = forwardRef<QuestionSettingsRef, QuestionSettingsProps>(
  ({ questionIndex, onDelete, autocompleteItems, canEdit }, ref) => {
    const { t } = useTranslation('playbook-custom-questions');
    const confirm = useConfirmationDialog();
    const logicRef = useRef<SettingsLogicRef>(null);
    const {
      control,
      formState: { dirtyFields },
    } = useFormContext<CustomQuestionsFormData>();
    const question = useWatch({ control, name: `questions.${questionIndex}` });
    const questions = useWatch({ control, name: 'questions' });
    const availableQuestions = (() => {
      if (!questions) {
        return [];
      }

      return questions.slice(0, questionIndex).filter((q): q is Exclude<typeof q, { kind: 'text_block' }> => {
        // Exclude nullish, text blocks, or questions missing a dataIdentifier
        if (!q) return false;
        if (q.kind === 'text_block') return false;
        if (!('dataIdentifier' in q)) return false;
        if (!q.dataIdentifier || typeof q.dataIdentifier !== 'string' || q.dataIdentifier.trim() === '') return false;
        return true;
      });
    })();
    const hasLogic = !!question?.condition;
    useFocusOnQuestion(questionIndex);

    const validateBeforeClose = async (): Promise<boolean> => {
      // Check if there are incomplete logic rules
      if (logicRef.current?.hasIncompleteRules()) {
        return new Promise<boolean>(resolve => {
          confirm.open({
            title: t($ => $.confirmLogic.title),
            description: t($ => $.confirmLogic.description),
            variant: 'destructive',
            primaryButton: {
              label: t($ => $.confirmLogic.removeLogic),
              onClick: () => {
                logicRef.current?.clearLogic();
                resolve(true);
              },
            },
            secondaryButton: {
              label: t($ => $.confirmLogic.keepEditing),
              onClick: () => {
                resolve(false);
              },
            },
          });
        });
      }

      return true;
    };

    useImperativeHandle(ref, () => ({
      validateBeforeClose,
    }));

    // Guard against undefined question during cleanup transitions (must be after all hooks)
    if (!question) {
      return null;
    }

    const handleLogicOpenChange = async (open: boolean) => {
      // If opening, enable logic by default
      if (open && !hasLogic) {
        logicRef.current?.enableLogic();
        return true;
      }

      // If closing and there are incomplete rules, just clear them
      if (logicRef.current?.hasIncompleteRules()) {
        logicRef.current?.clearLogic();
      }

      return true;
    };

    const handleDelete = () => {
      const isQuestionTouched = dirtyFields.questions?.[questionIndex];

      // If the question hasn't been modified, delete without confirmation
      if (!isQuestionTouched) {
        return onDelete();
      }

      confirm.open({
        title: t($ => $.confirmDelete.title),
        description: t($ => $.confirmDelete.description),
        variant: 'destructive',
        primaryButton: {
          label: t($ => $.confirmDelete.delete),
          onClick: onDelete,
        },
        secondaryButton: {
          label: t($ => $.confirmDelete.cancel),
        },
      });
    };

    return (
      <div className="bg-primary border border-tertiary rounded">
        <div className="flex flex-col">
          <SettingsSection title={t($ => $.questionSettings.basicSettings)} icon={IcoSettings16} defaultExpanded>
            {question.kind === 'text_block' && (
              <TextBlockForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'short_text' && (
              <ShortTextForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'long_text' && (
              <LongTextForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'number' && (
              <NumberForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'dropdown' && (
              <DropdownForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'checkboxes' && (
              <CheckboxesForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'checkbox' && (
              <CheckboxForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'multiple_choice' && (
              <MultipleChoiceForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
            {question.kind === 'address' && (
              <AddressForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />
            )}
          </SettingsSection>

          {availableQuestions.length > 0 && (
            <>
              <Divider />
              <SettingsSection
                title={t($ => $.questionSettings.logic)}
                icon={IcoShuffle16}
                defaultExpanded={hasLogic}
                onOpenChange={canEdit ? handleLogicOpenChange : undefined}
              >
                <SettingsLogic
                  ref={logicRef}
                  questionIndex={questionIndex}
                  availableQuestions={availableQuestions}
                  canEdit={canEdit}
                />
              </SettingsSection>
            </>
          )}

          {canEdit && (
            <>
              <Divider />
              <div className="p-3 pt-2">
                <LinkButton destructive onClick={handleDelete} iconComponent={IcoTrash16} iconPosition="left">
                  {t($ => $.questionSettings.deleteQuestion)}
                </LinkButton>
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
);

QuestionSettings.displayName = 'QuestionSettings';

export default QuestionSettings;
